/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { getDownloadURL, ref } from 'firebase/storage'
import { useLocation } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { BASE_COLLECTION, storage } from 'config/firebase'
import { useMaterialUIController } from 'context'
import CallInsightsContent from 'layouts/call-insights/components/CallInsightsContent'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { CallInsight, EnrichedCallInsight } from 'types/server/call-insights'

function CallInsights(): JSX.Element {
	const location = useLocation()
	const [controller] = useMaterialUIController()
	const { session } = controller

	const call_id = location.pathname.split('/')[2]

	const fetchData = async () => {
		const response = await axios.get<CallInsight>(`${process.env.REACT_APP_API_URL}/call`, {
			params: {
				call_id: call_id,
			},
			headers: {
				Authorization: await session.user.getIdToken(),
			},
		})
		const recordingRef = ref(
			storage,
			`${BASE_COLLECTION}/${session.data.organisation.id}/recordings/${response.data.call_id}.wav`
		)
		let recordingUrl = ''
		try {
			recordingUrl = await getDownloadURL(recordingRef)
		} catch {
			console.log('No recording found')
			recordingUrl = ''
		}
		return { ...response.data, recording_url: recordingUrl }
	}

	const exportToTxt = (filename: string, data: EnrichedCallInsight) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { recording_url, start_timestamp, end_timestamp, ...rest } = data || {}
		const parsedData = {
			date: new Date(start_timestamp).toLocaleString(undefined, {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			}),
			...rest,
		}
		const jsonData = JSON.stringify(parsedData, null, 2) // Convert object to JSON string with indentation
		const blob = new Blob([jsonData], { type: 'text/plain' })
		const url = URL.createObjectURL(blob)

		const a = document.createElement('a')
		a.href = url
		a.download = filename
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)

		URL.revokeObjectURL(url)
	}

	const { data, error, isLoading } = useQuery({ queryKey: [`call-${call_id}`], queryFn: fetchData })

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox mt={5} mb={9}>
				<Grid container justifyContent="center">
					<Grid item xs={12} lg={8}>
						<MDBox mt={6} mb={8} textAlign="center">
							<MDBox mb={1}>
								<MDTypography variant="h3" fontWeight="bold">
									Call Insights
								</MDTypography>
							</MDBox>
							<MDTypography variant="h5" fontWeight="regular" color="secondary">
								View the call information below.
							</MDTypography>
						</MDBox>
						<Card>
							<MDBox
								mt={-2}
								mx={2}
								borderRadius={'xxl'}
								height={'35px'}
								variant="gradient"
								bgColor="primary"
							/>
							{!isLoading && !error ? (
								<>
									<MDBox p={2}>
										<CallInsightsContent data={data} id={call_id} />
									</MDBox>
									<MDBox my={2} width="90%" display="flex" justifyContent="flex-end">
										<MDButton
											variant="contained"
											color="primary"
											onClick={() => exportToTxt(`call-${call_id}.txt`, data)}
										>
											export
										</MDButton>
									</MDBox>
								</>
							) : (
								<LoadingComponent type="box" />
							)}
						</Card>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	)
}

export default CallInsights
